import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSyncReports } from '../../../slice/sharepointSlice'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import { Loader2, RefreshCw, FileText } from 'lucide-react'
import ImportReportModal from './ImportReportModal'

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
}

function dotColor(r) {
  if ((r.error_count || 0) > 0) return '#DC2626'
  if ((r.skipped_count || 0) > 0) return '#D97706'
  return '#059669'
}

// Shared by "Import reports" and "Deletion protocol" — a deletion writes
// the exact same SharePointSyncReports shape as an import (report_type
// discriminates), so this one list view renders either kind: same layout,
// just different title/labels and which type it fetches.
export default function SyncReportsTab({ taskId = null, reportType = 'import' }) {
  const dispatch = useDispatch()
  const { aispaceColor } = useThemeColor()
  const { t } = useTranslation()
  const { syncReports, syncReportsLoading, syncReportsError } = useSelector(s => s.sharepoint)
  const [viewReport, setViewReport] = useState(null) // { reportId, siteName }
  const isDelete = reportType === 'delete'

  useEffect(() => {
    dispatch(fetchSyncReports({ task_id: taskId, report_type: reportType }))
  }, [dispatch, taskId, reportType])

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-[15px] font-bold text-[#111827]">
            {isDelete ? t('agentSettings.filesSubTabProtocol') : t('agentSettings.filesSubTabImportReports')}
          </h3>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            {isDelete ? t('agentSettings.filesDeletionReportsSubtitle') : t('agentSettings.filesImportReportsSubtitle')}
          </p>
        </div>
        <button
          onClick={() => dispatch(fetchSyncReports({ task_id: taskId, report_type: reportType }))}
          disabled={syncReportsLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg disabled:opacity-50"
        >
          {syncReportsLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {t('agentSettings.filesImportReportsRefresh')}
        </button>
      </div>

      {syncReportsError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {syncReportsError}
        </div>
      )}

      {syncReportsLoading && syncReports.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#EEEEEE] flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
        </div>
      ) : syncReports.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#EEEEEE] flex flex-col items-center justify-center py-16 text-center">
          <FileText size={36} className="text-[#D1D5DB] mb-3" />
          <p className="text-sm text-[#6B7280]">
            {isDelete ? t('agentSettings.filesDeletionReportsEmpty') : t('agentSettings.filesImportReportsEmpty')}
          </p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {isDelete ? t('agentSettings.filesDeletionReportsEmptyHint') : t('agentSettings.filesImportReportsEmptyHint')}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#EEEEEE] rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center bg-[#F9FAFB] border-b border-[#EEEEEE]">
            <div className="flex-1 min-w-0 py-3 px-6"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesImportReportsColSite')}</span></div>
            <div className="w-[160px] py-3 px-4"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesImportReportsColStarted')}</span></div>
            <div className="w-[120px] py-3 px-4 text-right">
              <span className="text-[13px] font-medium text-[#616161]">
                {isDelete ? t('agentSettings.filesImportReportRemoved') : t('agentSettings.filesImportReportImported')}
              </span>
            </div>
            <div className="w-[100px] py-3 px-4 text-right"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesImportReportSkipped')}</span></div>
            <div className="w-[100px] py-3 px-4 text-right"><span className="text-[13px] font-medium text-[#616161]">{t('agentSettings.filesImportReportFailed')}</span></div>
            <div className="w-[190px] py-3 px-4"></div>
          </div>
          {/* Rows */}
          {syncReports.map((r, idx) => (
            <div
              key={r.id}
              className={`flex items-center hover:bg-[#F9FAFB] transition-colors ${idx !== syncReports.length - 1 ? 'border-b border-[#EEEEEE]' : ''}`}
            >
              <div className="flex-1 min-w-0 py-4 px-6">
                <span className="text-[14px] font-medium text-[#111827] truncate block" title={r.site_name}>
                  {r.site_name || r.site_id || '—'}
                  {(r.report_type === 'stopped' || r.report_type === 'discarded') && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[11px] font-medium align-middle bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]">
                      {r.report_type === 'discarded' ? t('agentSettings.filesImportReportBadgeDiscarded') : t('agentSettings.filesImportReportBadgeStopped')}
                    </span>
                  )}
                </span>
              </div>
              <div className="w-[160px] py-4 px-4">
                <span className="text-[13px] text-[#616161]">{fmt(r.started_at)}</span>
              </div>
              <div className="w-[120px] py-4 px-4 text-right">
                <span className="inline-flex items-center gap-1.5 justify-end">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor(r) }} />
                  <span className="text-[14px] text-[#111827]">{r.synced_count ?? 0}</span>
                </span>
              </div>
              <div className="w-[100px] py-4 px-4 text-right">
                <span className="text-[13px] text-[#616161]">{r.skipped_count ?? 0}</span>
              </div>
              <div className="w-[100px] py-4 px-4 text-right">
                <span className="text-[13px] text-[#616161]">{r.error_count ?? 0}</span>
              </div>
              <div className="w-[190px] py-4 px-4 text-right">
                <button
                  onClick={() => setViewReport({ reportId: r.id, siteName: r.site_name })}
                  className="text-[13px] font-medium hover:underline whitespace-nowrap"
                  style={{ color: aispaceColor }}
                >
                  {isDelete ? t('agentSettings.filesViewDeletionReport') : t('agentSettings.filesViewImportReport')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewReport && (
        <ImportReportModal
          reportId={viewReport.reportId}
          siteName={viewReport.siteName}
          reportType={reportType}
          aispaceColor={aispaceColor}
          t={t}
          onClose={() => setViewReport(null)}
        />
      )}
    </div>
  )
}
